import { useEffect, useRef, useState } from 'react'

import { ReviewViewpointStl } from '../../../api/reviewTasks'
import { FloorProgressTranslation } from '../../../api/types'
import {
  applyViewerPositionDelta,
  calculateViewerPositionDelta,
} from '../../../utils'
import { getProgressRotation } from '../../../utils/progressFloorTransform'
import { LoadingIndicator } from '../../common/LoadingIndicator'
import { Pannellum } from '../../third_party/Pannellum'
import { ViewerPosition } from '../image_viewer/types'

import { ReviewStlOverlay } from './ReviewStlOverlay'
import { EmptyPane, PanoLayer, PanoLoadingOverlay, PanoStack } from './styles'

export const REVIEW_PANO_SYNC_ID = 'review-pano-sync'

export interface ReviewLoadedPane {
  imageUrl: string
  angle: number
  takenOn: string | null
}

interface ReviewPanoPaneProps {
  latest: ReviewLoadedPane | null
  previous: ReviewLoadedPane | null
  loading: boolean
  busy?: boolean
  historyEnabled: boolean
  /** Opacity of the prior (old) scan; 1 = old image only. */
  overlayOpacity: number
  overlayOffset: ViewerPosition
  onOverlayOffsetChange: (offset: ViewerPosition) => void
  viewerPosition: ViewerPosition
  onViewerPosition: (position: ViewerPosition) => void
  onPreviousReadyChange?: (ready: boolean) => void
  stlOverlayEnabled?: boolean
  stlOffset?: { x: number; y: number }
  onStlOffsetChange?: (offset: { x: number; y: number }) => void
  stlMeshes?: ReviewViewpointStl[]
  planX?: number | null
  planY?: number | null
  progressTranslation?: FloorProgressTranslation | null
}

export const ReviewPanoPane = ({
  latest,
  previous,
  loading,
  busy = false,
  historyEnabled,
  overlayOpacity,
  overlayOffset,
  onOverlayOffsetChange,
  viewerPosition,
  onViewerPosition,
  onPreviousReadyChange,
  stlOverlayEnabled = false,
  stlOffset = { x: 0, y: 0 },
  onStlOffsetChange,
  stlMeshes = [],
  planX = null,
  planY = null,
  progressTranslation = null,
}: ReviewPanoPaneProps) => {
  const [latestReady, setLatestReady] = useState(false)
  const [previousReady, setPreviousReady] = useState(false)
  /** Hold Control to pan/zoom the prior scan without moving the current scan. */
  const [overlayUnlocked, setOverlayUnlocked] = useState(false)
  const [previousPosition, setPreviousPosition] =
    useState<ViewerPosition>(viewerPosition)

  const viewerPositionRef = useRef(viewerPosition)
  const overlayOffsetRef = useRef(overlayOffset)
  const previousPositionRef = useRef(previousPosition)
  const overlayUnlockedRef = useRef(overlayUnlocked)
  const onOverlayOffsetChangeRef = useRef(onOverlayOffsetChange)
  const stlOffsetRef = useRef(stlOffset)
  const onStlOffsetChangeRef = useRef(onStlOffsetChange)
  const stlOverlayEnabledRef = useRef(stlOverlayEnabled)
  const progressTranslationRef = useRef(progressTranslation)
  viewerPositionRef.current = viewerPosition
  overlayOffsetRef.current = overlayOffset
  previousPositionRef.current = previousPosition
  overlayUnlockedRef.current = overlayUnlocked
  onOverlayOffsetChangeRef.current = onOverlayOffsetChange
  stlOffsetRef.current = stlOffset
  onStlOffsetChangeRef.current = onStlOffsetChange
  stlOverlayEnabledRef.current = stlOverlayEnabled
  progressTranslationRef.current = progressTranslation

  useEffect(() => {
    setLatestReady(false)
  }, [latest?.imageUrl])

  useEffect(() => {
    setPreviousReady(false)
    onPreviousReadyChange?.(false)
    setOverlayUnlocked(false)
    setPreviousPosition(
      applyViewerPositionDelta(
        viewerPositionRef.current,
        overlayOffsetRef.current,
        false,
      ),
    )
  }, [onPreviousReadyChange, previous?.imageUrl])

  useEffect(() => {
    onPreviousReadyChange?.(previousReady)
  }, [onPreviousReadyChange, previousReady])

  useEffect(() => {
    if (!historyEnabled) {
      setOverlayUnlocked(false)
    }
  }, [historyEnabled])

  useEffect(() => {
    if (!historyEnabled || !previous) {
      return
    }

    const lockOverlay = () => {
      if (!overlayUnlockedRef.current) {
        return
      }
      onOverlayOffsetChangeRef.current(
        calculateViewerPositionDelta(
          viewerPositionRef.current,
          previousPositionRef.current,
        ),
      )
      setOverlayUnlocked(false)
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.key !== 'Control' ||
        event.repeat ||
        overlayUnlockedRef.current
      ) {
        return
      }
      setPreviousPosition(
        applyViewerPositionDelta(
          viewerPositionRef.current,
          overlayOffsetRef.current,
          false,
        ),
      )
      setOverlayUnlocked(true)
    }

    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'Control') {
        lockOverlay()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('blur', lockOverlay)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('blur', lockOverlay)
    }
  }, [historyEnabled, previous])

  // Control+drag shifts the STL overlay in plan XY while 3D mode is on.
  useEffect(() => {
    if (!stlOverlayEnabled || !onStlOffsetChange) {
      return
    }

    let dragging = false
    let lastX = 0
    let lastY = 0

    const onPointerDown = (event: PointerEvent) => {
      if (!event.ctrlKey || !stlOverlayEnabledRef.current) {
        return
      }
      dragging = true
      lastX = event.clientX
      lastY = event.clientY
      event.preventDefault()
    }

    const onPointerMove = (event: PointerEvent) => {
      if (!dragging || !event.ctrlKey) {
        dragging = false
        return
      }
      const dx = event.clientX - lastX
      const dy = event.clientY - lastY
      lastX = event.clientX
      lastY = event.clientY

      // Map screen drag into plan XY relative to the current look direction:
      // right → screen-right, up → farther along look (away from camera).
      const rotation = getProgressRotation(progressTranslationRef.current) ?? 0
      const yawRad =
        ((viewerPositionRef.current.yaw + rotation) * Math.PI) / 180
      const forwardX = Math.sin(yawRad)
      const forwardY = Math.cos(yawRad)
      // World Z-up right = cross(up, forward) on the XY plane.
      const rightX = -forwardY
      const rightY = forwardX

      const scale = 0.1
      // Screen Y grows downward; negate so trackpad-up = farther away.
      const along = -dy * scale
      const across = dx * scale
      const current = stlOffsetRef.current
      onStlOffsetChangeRef.current?.({
        x: current.x + across * rightX + along * forwardX,
        y: current.y + across * rightY + along * forwardY,
      })
      event.preventDefault()
    }

    const onPointerUp = () => {
      dragging = false
    }

    window.addEventListener('pointerdown', onPointerDown, true)
    window.addEventListener('pointermove', onPointerMove, true)
    window.addEventListener('pointerup', onPointerUp, true)
    window.addEventListener('blur', onPointerUp)
    return () => {
      window.removeEventListener('pointerdown', onPointerDown, true)
      window.removeEventListener('pointermove', onPointerMove, true)
      window.removeEventListener('pointerup', onPointerUp, true)
      window.removeEventListener('blur', onPointerUp)
    }
  }, [onStlOffsetChange, stlOverlayEnabled])

  const showSpinner = loading || busy || (!!latest && !latestReady)

  if (!latest && !loading && !busy) {
    return <EmptyPane>No approved scan at this viewpoint.</EmptyPane>
  }

  const previousLook = overlayUnlocked
    ? previousPosition
    : applyViewerPositionDelta(viewerPosition, overlayOffset, false)

  const syncedTo =
    historyEnabled && previous && !overlayUnlocked
      ? REVIEW_PANO_SYNC_ID
      : undefined

  return (
    <>
      <PanoStack id={REVIEW_PANO_SYNC_ID}>
        {previous && (
          <PanoLayer
            $opacity={historyEnabled ? overlayOpacity : 0}
            $interactive={historyEnabled && overlayUnlocked}
            $hidden={!historyEnabled}
            $zIndex={historyEnabled ? 2 : 0}
            aria-hidden={!historyEnabled}
          >
            <Pannellum
              image={previous.imageUrl}
              viewerId="review-previous"
              isSolo
              viewerPosition={previousLook}
              angleOffset={previous.angle || 0}
              syncedTo={syncedTo}
              onUpdate={overlayUnlocked ? setPreviousPosition : undefined}
              onLoad={() => setPreviousReady(true)}
              minHfov={50}
              maxHfov={160}
            />
          </PanoLayer>
        )}
        {latest && (
          <PanoLayer $opacity={1} $interactive={!overlayUnlocked} $zIndex={1}>
            <Pannellum
              image={latest.imageUrl}
              viewerId="review-latest"
              isSolo
              viewerPosition={viewerPosition}
              angleOffset={latest.angle || 0}
              syncedTo={syncedTo}
              onUpdate={overlayUnlocked ? undefined : onViewerPosition}
              onLoad={() => setLatestReady(true)}
              minHfov={50}
              maxHfov={160}
            />
          </PanoLayer>
        )}
        {latest && (
          <ReviewStlOverlay
            enabled={stlOverlayEnabled && latestReady}
            meshes={stlMeshes}
            planX={planX}
            planY={planY}
            imageAngle={latest.angle || 0}
            progressTranslation={progressTranslation}
            viewerPosition={viewerPosition}
            offsetX={stlOffset.x}
            offsetY={stlOffset.y}
          />
        )}
      </PanoStack>
      {showSpinner && (
        <PanoLoadingOverlay>
          <LoadingIndicator />
        </PanoLoadingOverlay>
      )}
    </>
  )
}
